import { Injectable, inject } from '@angular/core';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, InteractionStatus, InteractionType, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, EMPTY, Subject, from } from 'rxjs';
import { catchError, filter, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { LoadingService } from './loading.service';
import { AuthCookieService } from './auth-cookie.service';
import { SharedService } from './shared.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private authService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private loadingService = inject(LoadingService);
  private authCookie = inject(AuthCookieService);
  private sharedService = inject(SharedService);

  loggedIn = false;
  public isLoggedIn$ = new BehaviorSubject<boolean>(false);
  public isCookieAttached$ = new BehaviorSubject<boolean>(false);
  private readonly _destroying$ = new Subject<void>();

  UpdateLoggedInStatus() {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        switchMap(() => {
          this.loadingService.ShowLoadingSpinner();
          this.CheckAndSetActiveAccount();
          this.SetLoggedIn();

          if (!this.loggedIn) {
            this.isCookieAttached$.next(false);
            this.sharedService.NavigateToLogin();
            this.loadingService.HideLoadingSpinner();
            return EMPTY;
          }

          return from(this.authCookie.AttachCookie()).pipe(
            tap(() => {
              this.isCookieAttached$.next(true);
              this.authCookie.CheckExpiryAndWarn();
              this.sharedService.NavigateToProjectList();
            }),
            catchError(() => {
              this.isCookieAttached$.next(false);
              this.sharedService.NavigateToLogin();
              return EMPTY;
            }),
            finalize(() => this.loadingService.HideLoadingSpinner())
          );
        }),
        takeUntil(this._destroying$)
      )
      .subscribe();
  }

  Login() {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.LoginWithPopup();
    } else {
      this.LoginWithRedirect();
    }
  }

  GetActiveAccount(): AccountInfo | null {
    return this.authService.instance.getActiveAccount();
  }

  private CheckAndSetActiveAccount() {
    /**
    * If no active account set but there are accounts signed in, sets first account to active account
    * To use active account set here, subscribe to inProgress$ first in your component
    * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
    */
    const activeAccount = this.authService.instance.getActiveAccount();
    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  private SetLoggedIn() {
    this.loggedIn = this.authService.instance.getAllAccounts().length > 0;
    this.isLoggedIn$.next(this.loggedIn);
  }

  private LoginWithPopup() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
        .pipe(takeUntil(this._destroying$))
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
    } else {
      this.authService.loginPopup()
        .pipe(takeUntil(this._destroying$))
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
    }
  }

  private LoginWithRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  Logout() {
    this.loggedIn = false;
    this.isLoggedIn$.next(this.loggedIn);
    this.authService.logoutRedirect();
    this.authCookie.LogoutCookie().then(() => {
      this.isCookieAttached$.next(false);
    });
  }

  async ClearAuthCookie(): Promise<void> {
    try {
      await this.authCookie.LogoutCookie();
    } finally {
      this.isCookieAttached$.next(false);
    }
  }

  Destroy() {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}